import { Box, Snackbar, TextField } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AccordionSection from '../../components/AccordionSection';
import EmailTemplatesSection, { EmailTemplateSectionModule } from '../../components/common/EmailTemplatesSection/EmailTemplatesSection';
import FormTemplatesSection from '../../components/common/FormTemplatesSection/FormTemplatesSection';
import ItemHeader from '../../components/common/ItemHeader/ItemHeader';
import MoreOptions from '../../components/common/MoreOptions/MoreOptions';
import TagsSection from '../../components/common/TagsSection/TagsSection';
import TemplateFieldsSection from '../../components/common/TemplateFieldsSection/TemplateFieldsSection';
import DuplicateModal from '../../components/DuplicateModal';
import { DependencyOption } from '../../components/tasks/StartCondition';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../../routes/Routes';
import { useCreateEventTemplate, useGetEventTemplateById, useUpdateEventTemplate } from '../../services/react-query/event_templates';
import { useArchiveEventTemplate } from '../../services/react-query/event_templates/useArchiveEventTemplate';
import { useDuplicateEventTemplate } from '../../services/react-query/event_templates/useDuplicateEventTemplate';
import { mutationKeys } from '../../services/react-query/mutationKeys';
import { inputContainerStyling, inputDividerStyling } from '../../styles/sxStyling';
import { EventTemplate, EventTemplateParentChild } from '../../types/EventTemplate';
import { TemplateField } from '../../types/FieldCommon';
import { UserSettingsCollapseKeys } from '../../types/User';
import { Loading } from '../../uikit';
import { errorPopulator } from '../../utility/inputUtils';
import EventTemplatesSection from './EventTemplatesSection';
import IntegrationSection from './IntegrationsSection';
import TaskTemplateSections from './TaskTemplatesSection';
import EmployeeRequirementSection from '../../components/common/EmployeeRequirementSection/EmployeeRequirementSection';

const googleCalendarDefaultColors = [
  { label: 'Default Value', color: '#fff', value: 0 },
  { label: 'Lavender', color: '#7986cb', value: 1 },
  { label: 'Sage', color: '#33b679', value: 2 },
  { label: 'Grape', color: '#8e24aa', value: 3 },
  { label: 'Flamingo', color: '#e67c73', value: 4 },
  { label: 'Banana', color: '#f6c026', value: 5 },
  { label: 'Tangerine', color: '#F4511E', value: 6 },
  { label: 'Peacock', color: '#039be5', value: 7 },
  { label: 'Graphite', color: '#616161', value: 8 },
  { label: 'Blueberry', color: '#3f51b5', value: 9 },
  { label: 'Basil', color: '#0b8043', value: 10 },
  { label: 'Tomato', color: '#d50000', value: 11 },
];

export default function EventTemplateItem() {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { mutate: archiveEventTemplate } = useArchiveEventTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.EVENT_TEMPLATES}`),
  });
  const [eventTemplate, setEventTemplate] = useState<EventTemplate | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const { data, isLoading } = useGetEventTemplateById({ uuid });

  const saveCallback = () => setSaved(true);
  const { mutate: createEventTemplate } = useCreateEventTemplate({ onSuccessCallback: saveCallback });
  const { mutate: updateEventTemplate } = useUpdateEventTemplate({ uuid, onSuccessCallback: saveCallback });
  const isCreateEventTemplateMutation = useIsMutating({ mutationKey: [mutationKeys.createEventTemplate] });
  const isUpdateEventTemplateMutation = useIsMutating({ mutationKey: [mutationKeys.updateEventTemplate] });

  const { mutate: duplicateTemplateMutation } = useDuplicateEventTemplate((newUuid) => {
    navigate(`/event-templates/${newUuid}`);
  });

  useEffect(() => {
    if (data) setEventTemplate(data);
  }, [data]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable('archived', !!eventTemplate?.discarded_at);

  if (isLoading || eventTemplate === null) return <Loading />;

  const setFields = (fields: TemplateField[]) => setEventTemplate({ ...eventTemplate, fields });
  const setEventTemplateChildren = (event_template_children: EventTemplateParentChild[]) => setEventTemplate({ ...eventTemplate, event_template_children });

  const handleSave = () => {
    if (uuid === 'new') {
      createEventTemplate(eventTemplate);
    } else {
      updateEventTemplate(eventTemplate);
    }
  };

  const handleDuplicate = (name: string) => {
    duplicateTemplateMutation({ uuid: _.toString(uuid), name });
  };

  const dependencyOptions: DependencyOption[] = (eventTemplate?.event_template_task_templates || []).map((t) => ({
    name: t.task_template_name,
    uuid: t.uuid,
  }));

  return (
    <Box>
      <ItemHeader
        title={`${uuid === 'new' ? 'Create' : 'Update'} Event Template`}
        archived={!!eventTemplate.discarded_at}
        onSave={handleSave}
        disableActions={Boolean(isCreateEventTemplateMutation || isUpdateEventTemplateMutation)}
        showMoreOptions={
          uuid !== 'new' ? (
            <MoreOptions
              onArchive={archiveEventTemplate}
              onDuplicate={() => setIsDuplicateModalOpen(true)}
              name={eventTemplate?.name}
              itemName="event template"
            />
          ) : null
        }
      />
      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.EVENT_TEMPLATE_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          <Box sx={inputDividerStyling}>
            <TextField
              id="name"
              label="Name"
              onChange={(event) => setEventTemplate(eventTemplate ? { ...eventTemplate, name: event.target.value } : null)}
              value={eventTemplate?.name ?? ''}
              disabled={disabled}
              {...errorPopulator(eventTemplate, 'name')}
            />
          </Box>
          <Box sx={inputDividerStyling}>
            <TextField
              id="description"
              label="Description"
              onChange={(event) => setEventTemplate(eventTemplate ? { ...eventTemplate, description: event.target.value } : null)}
              value={eventTemplate?.description ?? ''}
              disabled={disabled}
              {...errorPopulator(eventTemplate, 'description')}
            />
          </Box>
        </Box>

        {/* Hide colors for now */}
        {/* <Box sx={{ ...inputDividerStyling, display: 'flex', flexDirection: 'column' }}>
            <FormControl sx={{ my: 1 }}>
              <InputLabel>Color</InputLabel>
              <Select
                value={eventTemplate?.color ?? ''}
                onChange={(event) => setEventTemplate(eventTemplate ? { ...eventTemplate, color: event.target.value } : null)}
                input={<OutlinedInput label="Color" />}
                disabled={disabled}
              >
                {googleCalendarDefaultColors.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        backgroundColor: item.value === 0 ? 'transparent' : item.color,
                        paddingLeft: '4px',
                        borderRadius: '2px',
                        color: item.value === 0 ? '#000' : '#fff',
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> */}
      </AccordionSection>

      <IntegrationSection
        integrations={eventTemplate.integrations ?? []}
        setIntegrations={(integrations) => setEventTemplate({ ...eventTemplate, integrations })}
      />
      <EventTemplatesSection eventTemplateChildren={eventTemplate.event_template_children} setEventTemplateChildren={setEventTemplateChildren} />

      <TaskTemplateSections
        linkingModels={eventTemplate.event_template_task_templates}
        setLinkingModels={(linkingModels) => setEventTemplate({ ...eventTemplate, event_template_task_templates: linkingModels })}
      />
      <EmailTemplatesSection
        moduleType={EmailTemplateSectionModule.EVENT_TEMPLATE}
        linkingModels={eventTemplate.event_template_email_templates}
        setLinkingModels={(linkingModels) => setEventTemplate({ ...eventTemplate, event_template_email_templates: linkingModels })}
        collapseId={UserSettingsCollapseKeys.EVENT_TEMPLATE_EMAIL_TEMPLATES}
        availableCustomFields={_.map(eventTemplate.fields, 'label')}
        dependencyOptions={dependencyOptions}
      />
      <FormTemplatesSection
        linkingModels={eventTemplate.event_template_form_templates}
        setLinkingModels={(linkingModels) => setEventTemplate({ ...eventTemplate, event_template_form_templates: linkingModels })}
        collapseId={UserSettingsCollapseKeys.EVENT_TEMPLATE_FORM_TEMPLATES}
      />
      <EmployeeRequirementSection
        collapseId={UserSettingsCollapseKeys.EVENT_TEMPLATE_ITEM_EMPLOYEE_REQUIREMENTS}
        employeeRequirements={eventTemplate.employee_requirements || []}
        setEmployeeRequirements={(reqs) => setEventTemplate({ ...eventTemplate, employee_requirements: reqs })}
      />

      <TemplateFieldsSection collapseId={UserSettingsCollapseKeys.EVENT_TEMPLATE_ITEM_FIELDS} fields={eventTemplate.fields} setFields={setFields} />

      <TagsSection
        tags={eventTemplate.tags}
        collapseId={UserSettingsCollapseKeys.EVENT_TEMPLATE_ITEM_TAGS}
        setTags={(tags) => setEventTemplate({ ...eventTemplate, tags })}
      />
      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onConfirm={(name) => handleDuplicate(name)}
        defaultName={`${eventTemplate?.name ?? ''} Copy`}
        itemName="event template"
      />

      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Box>
  );
}
